/**
 * Shared resolution of automation rule fields to the form controls the
 * discrepancy screens render.
 *
 * A field is identified by its `fieldId`. The five standard kinds keep their
 * per-screen `AutomationFieldKind` -> control map; every other field is
 * resolved from `targetColumnName`, so a new custom column becomes usable
 * through a backend change only.
 */

import { AbstractControl, FormGroup } from '@angular/forms';

import {
    AutomationFieldKind,
    AutomationRule,
    AutomationRuleField,
    AutomationRuleTypeGroup,
} from '@models/automation';
import { AutomationValueKind } from '@models/automation-rules';
import { CustomColumn } from '@models/workflow/custom-column';
import { SelectOption } from '@models/shared.model';
import {
    findOptionByRuleValue,
    isNameBasedValueKind,
    serializeOptionValues,
    toRuleOptionValue,
} from './automation-option-value';
import { formatAutomationDate } from './automation-field-value';
import {
    getCustomColumnInputType,
    isCustomColumnDate,
    isCustomColumnListBox,
    toDateValue,
} from './custom-column.helpers';

/** Nested group holding one control per workflow custom column. */
export const CUSTOM_COLUMN_GROUP = 'customColumns';

/** Top-level control name of a standard field kind on a given screen. */
export type StandardControlNames = Partial<Record<AutomationFieldKind, string>>;

/** Value a control an automation field targets can hold. */
export type AutomationControlValue =
    | string
    | number
    | Date
    | (string | number)[]
    | null
    | undefined;

/**
 * What the option values of a field mean and which options the screen holds for
 * it, so a name based field is read and written by option name. A field without
 * options needs none of it.
 */
export interface AutomationOptionContext {
    valueKind?: AutomationValueKind | null;
    /**
     * Options currently loaded for the field. Rules are configured over every
     * repository, so a rule value may have no option here at all.
     */
    options?: SelectOption[];
}

/** Control an automation field reads from and writes to. */
export interface AutomationTarget {
    control: AbstractControl;
    /** Column metadata, `null` for the standard discrepancy fields. */
    column: CustomColumn | null;
}

/**
 * Control an automation field targets on this screen, or `null` when the
 * screen renders none: an unknown kind without target column, a column that is
 * not enabled for the workflow, or a field the screen does not render.
 *
 * Custom columns are matched case-insensitively: the api returns the canonical
 * enum casing while the controls are keyed by the name stored in the database.
 */
export function resolveAutomationTarget(
    field: AutomationRuleField,
    form: FormGroup,
    standardControls: StandardControlNames,
    customColumns: CustomColumn[],
): AutomationTarget | null {
    if (field.fieldKind !== AutomationFieldKind.None) {
        const controlName = standardControls[field.fieldKind];
        const control = controlName ? form.get(controlName) : null;
        return control ? { control, column: null } : null;
    }

    const targetColumnName = field.targetColumnName;
    if (!targetColumnName) return null;

    const column = customColumns.find(
        (col) =>
            col.columnName.toLowerCase() === targetColumnName.toLowerCase(),
    );
    if (!column) return null;

    const control = form.get([CUSTOM_COLUMN_GROUP, column.columnName]);
    return control ? { control, column } : null;
}

/**
 * Every rule of the instance, without its rule type. Only for the places that
 * treat the rules as a flat list (control listeners, current value maps and
 * the fixer dialogs); chained evaluation needs the grouping.
 */
export function flattenAutomationRules(
    groups: AutomationRuleTypeGroup[],
): AutomationRule[] {
    return groups.flatMap((group) => group.rules);
}

/** Distinct fields the rules use as an input, keyed by `fieldId`. */
export function automationInputFields(
    rules: AutomationRule[],
): Map<number, AutomationRuleField> {
    return fieldsById(rules.flatMap((rule) => rule.inputs));
}

/** Distinct fields the rules produce, keyed by `fieldId`. */
export function automationOutputFields(
    rules: AutomationRule[],
): Map<number, AutomationRuleField> {
    return fieldsById(rules.flatMap((rule) => rule.outputs));
}

/**
 * `fieldId` of the input field carrying `kind`, or `null` when the loaded rules
 * take no input of that kind.
 *
 * Input and output ids are looked up separately: `AutomationField.Kind` is not
 * unique, so the same standard kind can be configured twice with different
 * ids, one per role.
 */
export function automationInputFieldIdOfKind(
    rules: AutomationRule[],
    kind: AutomationFieldKind,
): number | null {
    return automationInputFieldIdsOfKind(rules, kind)[0] ?? null;
}

/**
 * Every `fieldId` consuming `kind`. Each rule type can declare its own field
 * for the same standard kind, so all of them have to be seeded and triggered
 * from the single control the screen renders for that kind.
 */
export function automationInputFieldIdsOfKind(
    rules: AutomationRule[],
    kind: AutomationFieldKind,
): number[] {
    if (kind === AutomationFieldKind.None) return [];

    return [...automationInputFields(rules).values()]
        .filter((field) => field.fieldKind === kind)
        .map((field) => field.fieldId);
}

/**
 * Every input `fieldId` resolving to the same control as `field`: the fields
 * of the same standard kind, or the fields targeting the same custom column.
 * A change of that control has to trigger all of them, because each rule type
 * can declare its own field for the control.
 */
export function automationInputFieldIdsSharingTarget(
    rules: AutomationRule[],
    field: AutomationRuleField,
): number[] {
    return fieldIdsSharingTarget(
        [...automationInputFields(rules).values()],
        field,
    );
}

/**
 * Every output `fieldId` resolving to the same control as `field`. A manual
 * edit of that control has to protect all of them, otherwise a rule type
 * declaring its own field id for the control overwrites the user's value.
 */
export function automationOutputFieldIdsSharingTarget(
    rules: AutomationRule[],
    field: AutomationRuleField,
): number[] {
    return fieldIdsSharingTarget(
        [...automationOutputFields(rules).values()],
        field,
    );
}

/**
 * Every input and output `fieldId` the screen renders with the same control as
 * `fieldId`, including it. A value produced for one of them is a value for all
 * of them, because each rule type declares its own field for the same standard
 * kind or discrepancy column.
 */
export function automationFieldIdsSharingTarget(
    rules: AutomationRule[],
    fieldId: number,
): number[] {
    const fields = new Map([
        ...automationInputFields(rules),
        ...automationOutputFields(rules),
    ]);

    const field = fields.get(fieldId);
    if (!field) return [fieldId];

    const ids = new Set(fieldIdsSharingTarget([...fields.values()], field));
    ids.add(fieldId);

    return [...ids];
}

/**
 * `fieldId` of the output field carrying `kind`, or `null` when the loaded
 * rules produce no output of that kind.
 */
export function automationOutputFieldIdOfKind(
    rules: AutomationRule[],
    kind: AutomationFieldKind,
): number | null {
    return automationOutputFieldIdsOfKind(rules, kind)[0] ?? null;
}

/**
 * Every `fieldId` producing `kind`. Each rule type can declare its own field
 * for the same standard kind, so a consumer of a kind that appears in several
 * rule types has to look at all of them.
 */
export function automationOutputFieldIdsOfKind(
    rules: AutomationRule[],
    kind: AutomationFieldKind,
): number[] {
    if (kind === AutomationFieldKind.None) return [];

    return [...automationOutputFields(rules).values()]
        .filter((field) => field.fieldKind === kind)
        .map((field) => field.fieldId);
}

/**
 * Control value as the rules express it, so a rule value and the form can be
 * compared. Empty values are `null`.
 *
 * @param optionContext - Option metadata of the field. A name based field is
 * expressed by the name of the selected option, so a selection whose option is
 * unknown here has no rule value at all.
 */
export function toAutomationRuleValue(
    target: AutomationTarget,
    value: AutomationControlValue,
    optionContext?: AutomationOptionContext,
): string | null {
    if (value == null || value === '') return null;

    if (isNameBasedValueKind(optionContext?.valueKind)) {
        return toNameBasedRuleValue(value, optionContext?.options ?? []);
    }

    if (Array.isArray(value)) {
        // A selection is a set, so it reads as the combination the api stores
        // rather than as the order the control was filled in.
        return serializeOptionValues(value, optionContext?.valueKind) || null;
    }

    const column = target.column;
    if (column && isCustomColumnDate(column)) {
        // Rules hold a date in the canonical automation format, not in the
        // iso timestamp the api payload of the column carries.
        return formatAutomationDate(value) || null;
    }

    return String(value);
}

/**
 * Rule value coerced to what a custom column control holds, or `undefined`
 * when it cannot be used and the current value must be kept.
 *
 * @param options - ListBox options of the column. Empty means the column has
 * no selectable option, whether none exist or they are not loaded yet, so a
 * ListBox value is never applied against it.
 * @param valueKind - What the rule value of a ListBox column means. A name is
 * resolved to the option of this discrepancy reading the same.
 */
export function toCustomColumnControlValue(
    column: CustomColumn,
    value: string,
    options: SelectOption[],
    valueKind?: AutomationValueKind | null,
): string | number | Date | undefined {
    if (isCustomColumnListBox(column) && isNameBasedValueKind(valueKind)) {
        return findOptionByRuleValue(options, value, valueKind)?.value;
    }

    if (isCustomColumnListBox(column)) {
        const optionId = Number(value);
        if (value.trim() === '' || Number.isNaN(optionId)) return undefined;
        // Rules are defined over every repository's options, so an option can
        // be invalid for this discrepancy.
        if (!options.some((option) => option.value === optionId))
            return undefined;
        return optionId;
    }

    if (isCustomColumnDate(column)) {
        return toDateValue(value) ?? undefined;
    }

    if (getCustomColumnInputType(column) === 'number') {
        const numeric = Number(value);
        if (value.trim() === '' || Number.isNaN(numeric)) return undefined;
        // Numeric controls are seeded as strings; a number would leave the
        // form permanently dirty.
        return String(numeric);
    }

    return value;
}

/**
 * The names of the selected options, out of the ids a control holds, or `null`
 * when any selection matches no loaded option: a selection that cannot be
 * named in full has no rule value, rather than one of the remaining names,
 * which would match a rule for a narrower selection.
 */
function toNameBasedRuleValue(
    value: Exclude<AutomationControlValue, null | undefined>,
    options: SelectOption[],
): string | null {
    if (Array.isArray(value)) {
        const names: string[] = [];

        for (const entry of value) {
            const name = toRuleOptionValue(
                options,
                entry,
                AutomationValueKind.Name,
            );
            if (name === null) return null;
            names.push(name);
        }

        return names.length
            ? serializeOptionValues(names, AutomationValueKind.Name)
            : null;
    }

    if (value instanceof Date) return null;

    return toRuleOptionValue(options, value, AutomationValueKind.Name);
}

/** Fields of `candidates` the screen renders with the same control as `field`. */
function fieldIdsSharingTarget(
    candidates: AutomationRuleField[],
    field: AutomationRuleField,
): number[] {
    if (field.fieldKind !== AutomationFieldKind.None) {
        return candidates
            .filter((candidate) => candidate.fieldKind === field.fieldKind)
            .map((candidate) => candidate.fieldId);
    }

    const columnName = field.targetColumnName?.toLowerCase();
    if (!columnName) return [field.fieldId];

    return candidates
        .filter(
            (candidate) =>
                candidate.fieldKind === AutomationFieldKind.None &&
                candidate.targetColumnName?.toLowerCase() === columnName,
        )
        .map((candidate) => candidate.fieldId);
}

function fieldsById(
    fields: AutomationRuleField[],
): Map<number, AutomationRuleField> {
    return new Map(fields.map((field) => [field.fieldId, field]));
}
